import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, LogOut, Plus, Pencil, Trash2, Copy, X, Loader2, ExternalLink, CalendarDays, CalendarRange, Users, Image as ImageIcon, Settings, Tag, LayoutDashboard, Home as HomeIcon, Phone, Sparkles, MessageSquareQuote, Inbox, Mail, Building2 } from "lucide-react";
import { api, imgUrl, uploadImage, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteData";

const RESOURCES = {
  courses: {
    label: "Calendario Corsi", api: "courses", titleField: "name", subtitle: (x) => `${x.duration_hours ? x.duration_hours + " ore" : "—"} · ${(x.price || x.price === 0) ? "€" + x.price : "—"} · ${(x.editions || []).length} ediz.`,
    fields: (cats) => [
      { key: "name", label: "Nome corso", type: "text" },
      { key: "category", label: "Categoria", type: "select", options: cats.map((c) => ({ value: c.key || c.id, label: c.label })) },
      { key: "type", label: "Tipologia", type: "select", options: [{ value: "full", label: "Corso completo" }, { value: "refresh", label: "Aggiornamento" }] },
      { key: "docente", label: "Docente", type: "text" },
      { key: "duration_hours", label: "Durata totale (ore)", type: "number" },
      { key: "price", label: "Prezzo (€)", type: "number" },
      { key: "editions", label: "Edizioni e giornate", type: "editions" },
      { key: "location", label: "Sede", type: "text" },
      { key: "description", label: "Descrizione", type: "textarea" },
      { key: "image", label: "Foto di copertina (opzionale)", type: "image" },
      { key: "bg_color", label: "Colore di sfondo card (opzionale, vuoto = bianco)", type: "color" },
      { key: "text_light", label: "Testo chiaro (per sfondi scuri)", type: "checkbox" },
    ],
  },
  categories: {
    label: "Categorie", api: "categories", titleField: "label", subtitle: (x) => x.id, swatch: true,
    fields: () => [
      { key: "label", label: "Nome categoria (es. Cyber Security)", type: "text" },
      { key: "color", label: "Colore", type: "color" },
      { key: "order", label: "Ordine di visualizzazione", type: "number" },
    ],
  },
  teachers: {
    label: "Docenti", api: "teachers", titleField: "name", subtitle: (x) => x.role,
    fields: () => [
      { key: "name", label: "Nome e cognome", type: "text" },
      { key: "role", label: "Ruolo", type: "text" },
      { key: "photo", label: "Fotografia", type: "image" },
      { key: "specializzazioni", label: "Specializzazioni — una per riga", type: "list" },
      { key: "bio", label: "Biografia", type: "textarea" },
      { key: "corsi", label: "Corsi tenuti — uno per riga", type: "list" },
      { key: "certificazioni", label: "Certificazioni — una per riga", type: "list" },
    ],
  },
  gallery: {
    label: "Gallery", api: "gallery", titleField: "title", subtitle: (x) => x.cat,
    fields: (cats, galleryCats) => [
      { key: "title", label: "Titolo", type: "text" },
      { key: "src", label: "Immagine", type: "image" },
      { key: "cat", label: "Categoria/Album", type: "select", options: galleryCats.map((v) => ({ value: v, label: v })) },
      { key: "description", label: "Descrizione", type: "textarea" },
      { key: "tall", label: "Immagine grande (verticale)", type: "checkbox" },
    ],
  },
  events: {
    label: "Eventi & Webinar", api: "events", listApi: "/events", titleField: "title", subtitle: (x) => `${x.type} · ${x.date}${x.published === false ? " · NASCOSTO" : ""}`,
    fields: () => [
      { key: "title", label: "Titolo", type: "text" },
      { key: "type", label: "Tipo", type: "select", options: ["Webinar", "Workshop", "Seminario", "Open Day"].map((v) => ({ value: v, label: v })) },
      { key: "image", label: "Immagine", type: "image" },
      { key: "description", label: "Descrizione", type: "textarea" },
      { key: "relatore", label: "Relatore", type: "text" },
      { key: "date", label: "Data", type: "date" },
      { key: "orario", label: "Orario", type: "text" },
      { key: "modalita", label: "Modalità", type: "select", options: [{ value: "online", label: "Online" }, { value: "presenza", label: "In presenza" }] },
      { key: "posti", label: "Posti disponibili", type: "number" },
      { key: "link_iscrizione", label: "Link iscrizione (URL - opzionale, es. Zoom/Eventbrite)", type: "text" },
      { key: "published", label: "Pubblicato (visibile sul sito)", type: "checkbox" },
    ],
  },
  sm_areas: {
    label: "SempliceMente · Aree", api: "sm_areas", listApi: "/sm/areas", titleField: "name", subtitle: (x) => x.id, swatch: true,
    fields: () => [
      { key: "name", label: "Nome area (es. Leadership)", type: "text" },
      { key: "color", label: "Colore", type: "color" },
      { key: "order", label: "Ordine di visualizzazione", type: "number" },
    ],
  },
  sm_percorsi: {
    label: "SempliceMente · Percorsi", api: "sm_percorsi", listApi: "/sm/percorsi?published=false", titleField: "title",
    subtitle: (x) => `${x.format || "—"} · ${x.area || "—"}${x.is_published === false ? " · NASCOSTO" : ""}`, imgField: "cover",
    fields: (cats, gc, extra = {}) => [
      { key: "title", label: "Titolo percorso", type: "text" },
      { key: "slug", label: "Slug URL (es. leadership)", type: "text" },
      { key: "area", label: "Area tematica", type: "select", options: (extra.smAreas || []).map((a) => ({ value: a.id, label: a.name })) },
      { key: "format", label: "Formato", type: "select", options: ["Workshop", "Percorso aziendale", "Coaching 1:1", "Webinar", "Team building"].map((v) => ({ value: v, label: v })) },
      { key: "icon", label: "Icona (nome lucide, es. Crown)", type: "text" },
      { key: "cover", label: "Immagine di copertina", type: "image" },
      { key: "description", label: "Descrizione", type: "textarea" },
      { key: "obiettivi", label: "Obiettivi — uno per riga", type: "list" },
      { key: "benefici", label: "Benefici — uno per riga", type: "list" },
      { key: "moduli", label: "Programma / Moduli — uno per riga", type: "list" },
      { key: "destinatari", label: "Destinatari", type: "text" },
      { key: "durata", label: "Durata", type: "text" },
      { key: "modalita", label: "Modalità", type: "text" },
      { key: "teacher_id", label: "Docente collegato", type: "select", options: (extra.teachers || []).map((t) => ({ value: t.id, label: t.name })) },
      { key: "order", label: "Ordine di visualizzazione", type: "number" },
      { key: "is_published", label: "Pubblicato (visibile sul sito)", type: "checkbox" },
    ],
  },
  sm_sessions: {
    label: "SempliceMente · Sessioni", api: "sm_sessions", listApi: "/sm/sessions?published=false", titleField: "title",
    subtitle: (x) => `${x.date || ""} · ${x.mode || ""}${x.is_published === false ? " · NASCOSTO" : ""}`,
    fields: (cats, gc, extra = {}) => [
      { key: "title", label: "Titolo sessione", type: "text" },
      { key: "percorso", label: "Percorso collegato", type: "select", options: (extra.smPercorsi || []).map((p) => ({ value: p.title, label: p.title })) },
      { key: "date", label: "Data", type: "date" },
      { key: "time", label: "Orario (es. 09:30 - 13:30)", type: "text" },
      { key: "mode", label: "Modalità", type: "select", options: [{ value: "presenza", label: "In presenza" }, { value: "online", label: "Online" }] },
      { key: "location", label: "Sede", type: "text" },
      { key: "seats", label: "Posti", type: "number" },
      { key: "is_published", label: "Pubblicato (visibile sul sito)", type: "checkbox" },
    ],
  },
  sm_testimonials: {
    label: "SempliceMente · Testimonianze", api: "sm_testimonials", listApi: "/sm/testimonials?published=false", titleField: "author",
    subtitle: (x) => `${x.role || ""}${x.company ? " · " + x.company : ""}${x.is_published === false ? " · NASCOSTO" : ""}`, imgField: "avatar",
    fields: () => [
      { key: "author", label: "Nome", type: "text" },
      { key: "role", label: "Ruolo", type: "text" },
      { key: "company", label: "Azienda", type: "text" },
      { key: "quote", label: "Testimonianza", type: "textarea" },
      { key: "avatar", label: "Foto (opzionale)", type: "image" },
      { key: "order", label: "Ordine di visualizzazione", type: "number" },
      { key: "is_published", label: "Pubblicato (visibile sul sito)", type: "checkbox" },
    ],
  },
};

const GALLERY_CATS = ["Formazione obbligatoria", "SempliceMente", "Eventi", "Webinar", "Team Building", "Leadership"];

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "courses", label: "Calendario Corsi", icon: CalendarDays },
  { key: "categories", label: "Categorie", icon: Tag },
  { key: "teachers", label: "Docenti", icon: Users },
  { key: "gallery", label: "Gallery", icon: ImageIcon },
  { key: "events", label: "Eventi & Webinar", icon: CalendarRange },
  { key: "sm_percorsi", label: "SM · Percorsi", icon: Sparkles },
  { key: "sm_areas", label: "SM · Aree", icon: Tag },
  { key: "sm_sessions", label: "SM · Sessioni", icon: CalendarDays },
  { key: "sm_testimonials", label: "SM · Testimonianze", icon: MessageSquareQuote },
  { key: "sm_leads", label: "SM · Richieste", icon: Inbox },
  { key: "homepage", label: "Homepage", icon: HomeIcon },
  { key: "contatti", label: "Contatti & Social", icon: Phone },
  { key: "settings", label: "Impostazioni", icon: Settings },
];

const CONTENT_FIELDS = [
  { key: "session_name", label: "Nome sessione calendario (es. III Sessione 2026)" },
  { key: "hero_badge", label: "Hero — etichetta" },
  { key: "hero_title", label: "Hero — titolo" },
  { key: "hero_subtitle", label: "Hero — sottotitolo", area: true },
  { key: "cta_book", label: "Pulsante 1 (prenota)" },
  { key: "cta_download", label: "Pulsante 2 (scarica)" },
  { key: "cat_eyebrow", label: "Sezione Categorie — occhiello" },
  { key: "cat_title", label: "Sezione Categorie — titolo" },
  { key: "cat_subtitle", label: "Sezione Categorie — sottotitolo", area: true },
  { key: "courses_eyebrow", label: "Sezione Corsi — occhiello" },
  { key: "courses_title", label: "Sezione Corsi — titolo" },
  { key: "courses_subtitle", label: "Sezione Corsi — sottotitolo", area: true },
  { key: "footer_about", label: "Footer — descrizione azienda", area: true },
];

const CONTACT_FIELDS = [
  { key: "instagram", label: "Instagram (URL)" },
  { key: "facebook", label: "Facebook (URL)" },
  { key: "linkedin", label: "LinkedIn (URL)" },
  { key: "youtube", label: "YouTube (URL)" },
  { key: "tiktok", label: "TikTok (URL)" },
  { key: "website", label: "Sito web" },
  { key: "phone", label: "Telefono" },
  { key: "email", label: "Email" },
  { key: "address", label: "Indirizzo" },
  { key: "maps_query", label: "Indirizzo per la mappa (Google Maps)" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { cats, refresh: refreshSite } = useSite();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState("dashboard");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [confirmDel, setConfirmDel] = React.useState(null);

  const cfg = RESOURCES[tab];
  const isResource = !!cfg;
  const [refData, setRefData] = React.useState({ teachers: [], smAreas: [], smPercorsi: [] });

  const loadRef = React.useCallback(() => {
    Promise.all([api.get("/teachers"), api.get("/sm/areas"), api.get("/sm/percorsi?published=false")])
      .then(([t, a, p]) => setRefData({ teachers: t.data, smAreas: a.data, smPercorsi: p.data }))
      .catch(() => {});
  }, []);
  React.useEffect(() => { loadRef(); }, [loadRef]);

  const load = React.useCallback(() => {
    const c = RESOURCES[tab];
    if (!c) return;
    setLoading(true);
    api.get(c.listApi || `/${c.api}`).then((r) => setItems(tab === "courses" ? r.data.courses : r.data)).finally(() => setLoading(false));
  }, [tab]);

  React.useEffect(() => { load(); }, [load]);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const afterWrite = () => {
    load();
    if (tab === "categories") refreshSite();
    if (tab.startsWith("sm_")) loadRef();
  };

  const save = async (payload) => {
    if (tab === "courses") payload.duration_label = `${payload.duration_hours} ore`;
    if (payload.id) await api.put(`/admin/${cfg.api}/${payload.id}`, payload);
    else await api.post(`/admin/${cfg.api}`, payload);
    setEditing(null);
    afterWrite();
  };

  const duplicate = async (it) => {
    let src = it;
    if (tab === "courses") { try { src = (await api.get(`/courses/${it.id}`)).data; } catch (e) { /* keep it */ } }
    const copy = { ...src }; delete copy.id;
    copy[cfg.titleField] = `${src[cfg.titleField]} (copia)`;
    await api.post(`/admin/${cfg.api}`, copy);
    afterWrite();
  };

  const openEdit = async (it) => {
    if (tab === "courses") {
      try { const r = await api.get(`/courses/${it.id}`); setEditing(r.data); return; } catch (e) { /* fallback */ }
    }
    setEditing(it);
  };

  const remove = async (id) => {
    await api.delete(`/admin/${cfg.api}/${id}`);
    setConfirmDel(null);
    afterWrite();
  };

  const activeLabel = NAV.find((n) => n.key === tab)?.label || "";

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-64 md:min-h-screen bg-white border-b md:border-b-0 md:border-r border-gray-100 md:sticky md:top-0 md:h-screen flex md:flex-col">
        <div className="hidden md:flex items-center gap-2.5 px-5 h-16 border-b border-gray-100">
          <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#EA6A0A] text-white"><ShieldCheck size={20} /></span>
          <div><p className="font-display font-700 text-[#EA6A0A] leading-none text-sm">Rischio Zero</p><p className="text-[11px] text-gray-400 mt-0.5">Admin</p></div>
        </div>
        <nav className="flex md:flex-col gap-1 p-2 md:p-3 overflow-x-auto md:overflow-visible flex-1" data-testid="admin-sidebar">
          {NAV.map((n) => {
            const active = tab === n.key; const Icon = n.icon;
            return (
              <button key={n.key} data-testid={`admin-tab-${n.key}`} onClick={() => setTab(n.key)}
                className={`inline-flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-600 whitespace-nowrap transition-all ${active ? "bg-[#EA6A0A] text-white" : "text-gray-600 hover:bg-gray-100"}`}>
                <Icon size={17} /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="hidden md:block p-3 border-t border-gray-100">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm font-600 text-gray-500 hover:text-[#EA6A0A] px-3.5 py-2"><ExternalLink size={15} /> Vedi il sito</a>
          <button data-testid="admin-logout" onClick={doLogout} className="w-full flex items-center gap-2 text-sm font-600 text-gray-600 hover:text-[#EF4444] px-3.5 py-2 rounded-xl hover:bg-red-50 transition-colors"><LogOut size={15} /> Esci</button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
          <div className="md:hidden flex justify-end mb-4">
            <button onClick={doLogout} className="inline-flex items-center gap-1.5 text-sm font-600 text-gray-600"><LogOut size={15} /> Esci</button>
          </div>

          {tab === "dashboard" && <DashboardPanel go={setTab} />}
          {tab === "homepage" && <SettingsForm title="Contenuti Homepage" subtitle="Modifica i testi della homepage: hero, titoli delle sezioni, nome della sessione e testo del footer." fields={CONTENT_FIELDS} onSaved={refreshSite} />}
          {tab === "contatti" && <SettingsForm title="Contatti & Social" subtitle="Link social (lascia vuoto per nasconderli), telefono, email, indirizzo e mappa." fields={CONTACT_FIELDS} onSaved={refreshSite} />}
          {tab === "sm_leads" && <LeadsPanel />}

          {isResource && (
            <>
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display font-800 text-2xl tracking-tight text-gray-900">{cfg.label} <span className="text-gray-300">({items.length})</span></h2>
                <button data-testid="admin-add" onClick={() => setEditing({})}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#EA6A0A] text-white text-sm font-600 shadow-[0_6px_18px_-6px_rgba(234,106,10,0.5)] hover:bg-[#C8590A] transition-all">
                  <Plus size={17} /> Aggiungi
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#EA6A0A]" size={28} /></div>
              ) : (
                <div className="grid grid-cols-1 gap-2" data-testid="admin-list">
                  {items.map((it) => (
                    <div key={it.id} data-testid={`admin-item-${it.id}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-4 hover:shadow-sm transition-shadow">
                      {cfg.swatch ? (
                        <span className="w-10 h-10 rounded-xl shrink-0 border border-gray-100" style={{ background: it.color }} />
                      ) : (it.photo || it.src || it.image || it.cover || it.avatar) ? (
                        <img src={imgUrl(it.photo || it.src || it.image || it.cover || it.avatar)} alt="" loading="lazy" className="w-12 h-12 rounded-xl object-cover bg-gray-100 shrink-0" />
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <p className="font-600 text-gray-900 truncate">{it[cfg.titleField]}</p>
                        <p className="text-sm text-gray-400 truncate">{cfg.subtitle(it)}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button data-testid={`admin-duplicate-${it.id}`} title="Duplica" onClick={() => duplicate(it)} className="w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-[#EA6A0A] transition-colors"><Copy size={16} /></button>
                        <button data-testid={`admin-edit-${it.id}`} title="Modifica" onClick={() => openEdit(it)} className="w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-[#EA6A0A] transition-colors"><Pencil size={16} /></button>
                        <button data-testid={`admin-delete-${it.id}`} title="Elimina" onClick={() => setConfirmDel(it)} className="w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:bg-red-50 hover:text-[#EF4444] transition-colors"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <p className="text-center text-gray-400 py-16">Nessun elemento. Clicca "Aggiungi" per crearne uno.</p>}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <AnimatePresence>
        {editing && <FormModal cfg={cfg} initial={editing} cats={cats} galleryCats={GALLERY_CATS} extra={refData} onClose={() => setEditing(null)} onSave={save} />}
      </AnimatePresence>

      <AnimatePresence>
        {confirmDel && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDel(null)} />
            <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }} className="relative bg-white rounded-3xl p-7 max-w-sm w-full shadow-2xl">
              <h3 className="font-display font-700 text-lg text-gray-900">Eliminare questo elemento?</h3>
              <p className="text-sm text-gray-500 mt-1">"{confirmDel[cfg?.titleField]}" verrà eliminato definitivamente.</p>
              <div className="mt-6 flex gap-3">
                <button onClick={() => setConfirmDel(null)} className="flex-1 py-2.5 rounded-full border border-gray-200 font-600 text-gray-600 hover:bg-gray-50">Annulla</button>
                <button data-testid="admin-confirm-delete" onClick={() => remove(confirmDel.id)} className="flex-1 py-2.5 rounded-full bg-[#EF4444] text-white font-600 hover:bg-red-600">Elimina</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DashboardPanel({ go }) {
  const [c, setC] = React.useState({});
  React.useEffect(() => {
    Promise.all([api.get("/courses"), api.get("/events"), api.get("/teachers"), api.get("/gallery"), api.get("/categories")])
      .then(([co, ev, te, ga, ca]) => setC({ courses: co.data.courses.length, events: ev.data.length, teachers: te.data.length, gallery: ga.data.length, categories: ca.data.length }));
  }, []);
  const cards = [
    { k: "courses", label: "Corsi", n: c.courses, icon: CalendarDays },
    { k: "categories", label: "Categorie", n: c.categories, icon: Tag },
    { k: "teachers", label: "Docenti", n: c.teachers, icon: Users },
    { k: "events", label: "Eventi & Webinar", n: c.events, icon: CalendarRange },
    { k: "gallery", label: "Foto Gallery", n: c.gallery, icon: ImageIcon },
  ];
  return (
    <div data-testid="admin-dashboard-panel">
      <h2 className="font-display font-800 text-2xl tracking-tight text-gray-900 mb-1">Benvenuto 👋</h2>
      <p className="text-sm text-gray-500 mb-6">Gestisci tutti i contenuti del sito da qui. Seleziona una sezione dal menu a sinistra.</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((x) => (
          <button key={x.k} onClick={() => go(x.k)} className="text-left bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
            <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#EA6A0A]/10 text-[#EA6A0A] mb-3"><x.icon size={18} /></span>
            <p className="font-display font-700 text-2xl text-gray-900">{x.n ?? "—"}</p>
            <p className="text-sm text-gray-500">{x.label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function LeadsPanel() {
  const [items, setItems] = React.useState(null);
  const [confirmDel, setConfirmDel] = React.useState(null);
  const load = React.useCallback(() => { api.get("/admin/sm-leads").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  React.useEffect(() => { load(); }, [load]);

  const setStatus = async (it, status) => { await api.put(`/admin/sm_leads/${it.id}`, { ...it, status }); load(); };
  const remove = async (id) => { await api.delete(`/admin/sm_leads/${id}`); setConfirmDel(null); load(); };

  if (!items) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#EA6A0A]" size={28} /></div>;

  const STATUS = { new: { label: "Nuova", cls: "bg-[#EA6A0A]/10 text-[#EA6A0A]" }, handled: { label: "Gestita", cls: "bg-[#2FA84F]/10 text-[#2FA84F]" } };

  return (
    <div data-testid="leads-panel">
      <h2 className="font-display font-800 text-2xl tracking-tight text-gray-900 mb-1">Richieste SempliceMente <span className="text-gray-300">({items.length})</span></h2>
      <p className="text-sm text-gray-500 mb-6">Richieste inviate dal form "Richiedi un percorso su misura".</p>
      <div className="grid grid-cols-1 gap-3" data-testid="leads-list">
        {items.map((it) => {
          const st = STATUS[it.status] || STATUS.new;
          return (
            <div key={it.id} data-testid={`lead-${it.id}`} className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-700 text-gray-900">{it.name} <span className={`ml-2 text-xs font-600 px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span></p>
                  <p className="text-sm text-gray-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    <span className="inline-flex items-center gap-1"><Mail size={13} /> {it.email}</span>
                    {it.phone && <span className="inline-flex items-center gap-1"><Phone size={13} /> {it.phone}</span>}
                    {it.company && <span className="inline-flex items-center gap-1"><Building2 size={13} /> {it.company}</span>}
                  </p>
                  {it.topic && <p className="text-sm text-gray-600 mt-2"><b>Percorso:</b> {it.topic}{it.participants ? ` · ${it.participants} partecipanti` : ""}</p>}
                  {it.message && <p className="text-sm text-gray-500 mt-1 italic">"{it.message}"</p>}
                  <p className="text-xs text-gray-300 mt-2">{(it.created_at || "").slice(0, 10)}</p>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {it.status !== "handled"
                    ? <button data-testid={`lead-handle-${it.id}`} onClick={() => setStatus(it, "handled")} className="text-xs font-600 text-[#2FA84F] px-3 py-1.5 rounded-full hover:bg-[#2FA84F]/10">Segna gestita</button>
                    : <button onClick={() => setStatus(it, "new")} className="text-xs font-600 text-gray-500 px-3 py-1.5 rounded-full hover:bg-gray-100">Riapri</button>}
                  <button data-testid={`lead-delete-${it.id}`} onClick={() => setConfirmDel(it)} className="text-xs font-600 text-[#EF4444] px-3 py-1.5 rounded-full hover:bg-red-50 inline-flex items-center gap-1 justify-center"><Trash2 size={13} /> Elimina</button>
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="text-center text-gray-400 py-16">Nessuna richiesta ricevuta.</p>}
      </div>

      <AnimatePresence>
        {confirmDel && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDel(null)} />
            <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }} className="relative bg-white rounded-3xl p-7 max-w-sm w-full shadow-2xl">
              <h3 className="font-display font-700 text-lg text-gray-900">Eliminare questa richiesta?</h3>
              <p className="text-sm text-gray-500 mt-1">La richiesta di "{confirmDel.name}" verrà eliminata definitivamente.</p>
              <div className="mt-6 flex gap-3">
                <button onClick={() => setConfirmDel(null)} className="flex-1 py-2.5 rounded-full border border-gray-200 font-600 text-gray-600 hover:bg-gray-50">Annulla</button>
                <button data-testid="lead-confirm-delete" onClick={() => remove(confirmDel.id)} className="flex-1 py-2.5 rounded-full bg-[#EF4444] text-white font-600 hover:bg-red-600">Elimina</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SettingsForm({ title, subtitle, fields, onSaved }) {
  const [form, setForm] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  React.useEffect(() => { api.get("/settings").then((r) => setForm(r.data)); }, []);
  if (!form) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#EA6A0A]" size={28} /></div>;
  const set = (k, v) => { setForm((s) => ({ ...s, [k]: v })); setSaved(false); };
  const save = async () => {
    setBusy(true);
    const payload = { id: "site" };
    fields.forEach((f) => { payload[f.key] = form[f.key] ?? ""; });
    try { await api.put("/admin/settings", payload); setSaved(true); onSaved && onSaved(); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-xl" data-testid="settings-panel">
      <h2 className="font-display font-800 text-2xl tracking-tight text-gray-900 mb-1">{title}</h2>
      <p className="text-sm text-gray-500 mb-6">{subtitle}</p>
      <div className="space-y-4">
        {fields.map((f) => (
          <div key={f.key}>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide">{f.label}</label>
            {f.area ? (
              <textarea data-testid={`settings-${f.key}`} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} rows={2}
                className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#EA6A0A]/20 focus:border-[#EA6A0A]" />
            ) : (
              <input data-testid={`settings-${f.key}`} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#EA6A0A]/20 focus:border-[#EA6A0A]" />
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 flex items-center gap-3">
        <button data-testid="settings-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#EA6A0A] text-white font-600 hover:bg-[#C8590A] disabled:opacity-60 transition-all">
          {busy && <Loader2 size={16} className="animate-spin" />} Salva
        </button>
        {saved && <span data-testid="settings-saved" className="text-sm text-[#2FA84F] font-600">✓ Salvato</span>}
      </div>
    </div>
  );
}

const rid = () => Math.random().toString(36).slice(2, 9);
const emptyGiornata = () => ({ date: "", start: "", end: "", start2: "", end2: "" });
const STATUS_OPTS = [{ value: "available", label: "Posti disponibili" }, { value: "last", label: "Ultimi posti" }, { value: "full", label: "Completo" }];

function EditionsEditor({ value, onChange }) {
  const editions = value.length ? value : [{ id: rid(), status: "available", giornate: [emptyGiornata()] }];

  const update = (next) => onChange(next);
  const setEdition = (ei, patch) => update(editions.map((e, i) => (i === ei ? { ...e, ...patch } : e)));
  const setGiornate = (ei, giornate) => setEdition(ei, { giornate });
  const addEdition = () => update([...editions, { id: rid(), status: "available", giornate: [emptyGiornata()] }]);
  const removeEdition = (ei) => update(editions.filter((_, i) => i !== ei));
  const addGiornata = (ei) => setGiornate(ei, [...(editions[ei].giornate || []), emptyGiornata()]);
  const removeGiornata = (ei, gi) => setGiornate(ei, editions[ei].giornate.filter((_, i) => i !== gi));
  const setG = (ei, gi, key, val) => setGiornate(ei, editions[ei].giornate.map((g, i) => (i === gi ? { ...g, [key]: val } : g)));

  const timeCls = "px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EA6A0A]/20 focus:border-[#EA6A0A]";

  return (
    <div className="space-y-4" data-testid="editions-editor">
      {editions.map((ed, ei) => (
        <div key={ed.id || ei} className="rounded-2xl border border-gray-200 bg-gray-50/60 p-4">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <span className="text-xs font-700 uppercase tracking-wide text-[#EA6A0A]">Edizione {ei + 1}</span>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Posti:</label>
              <select data-testid={`edition-status-${ei}`} value={ed.status || "available"} onChange={(e) => setEdition(ei, { status: e.target.value })}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#EA6A0A]/20 focus:border-[#EA6A0A]">
                {STATUS_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {editions.length > 1 && (
                <button type="button" data-testid={`edition-remove-${ei}`} onClick={() => removeEdition(ei)} className="text-xs font-600 text-[#EF4444] inline-flex items-center gap-1 hover:underline"><Trash2 size={13} /> Rimuovi</button>
              )}
            </div>
          </div>
          <div className="space-y-3">
            {(ed.giornate || []).map((g, gi) => {
              const hasPm = !!(g.pm || g.start2 || g.end2);
              return (
                <div key={gi} className="bg-white rounded-xl border border-gray-100 p-3" data-testid={`giornata-${ei}-${gi}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-700 text-gray-400">Giornata {gi + 1}</span>
                    {(ed.giornate.length > 1) && (
                      <button type="button" data-testid={`giornata-remove-${ei}-${gi}`} onClick={() => removeGiornata(ei, gi)} className="text-gray-400 hover:text-[#EF4444]"><X size={14} /></button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="date" data-testid={`giornata-date-${ei}-${gi}`} value={g.date || ""} onChange={(e) => setG(ei, gi, "date", e.target.value)} className={timeCls} />
                    <span className="text-xs text-gray-400">dalle</span>
                    <input type="time" data-testid={`giornata-start-${ei}-${gi}`} value={g.start || ""} onChange={(e) => setG(ei, gi, "start", e.target.value)} className={timeCls} />
                    <span className="text-xs text-gray-400">alle</span>
                    <input type="time" data-testid={`giornata-end-${ei}-${gi}`} value={g.end || ""} onChange={(e) => setG(ei, gi, "end", e.target.value)} className={timeCls} />
                  </div>
                  {hasPm ? (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className="text-xs text-gray-400">pom. dalle</span>
                      <input type="time" data-testid={`giornata-start2-${ei}-${gi}`} value={g.start2 || ""} onChange={(e) => setG(ei, gi, "start2", e.target.value)} className={timeCls} />
                      <span className="text-xs text-gray-400">alle</span>
                      <input type="time" data-testid={`giornata-end2-${ei}-${gi}`} value={g.end2 || ""} onChange={(e) => setG(ei, gi, "end2", e.target.value)} className={timeCls} />
                      <button type="button" onClick={() => setGiornate(ei, editions[ei].giornate.map((gg, i) => (i === gi ? { ...gg, pm: false, start2: "", end2: "" } : gg)))} className="text-xs text-gray-400 hover:text-[#EF4444]">rimuovi pomeriggio</button>
                    </div>
                  ) : (
                    <button type="button" data-testid={`giornata-addpm-${ei}-${gi}`} onClick={() => setG(ei, gi, "pm", true)} className="mt-2 text-xs font-600 text-[#EA6A0A] hover:underline">+ aggiungi pomeriggio</button>
                  )}
                </div>
              );
            })}
          </div>
          <button type="button" data-testid={`giornata-add-${ei}`} onClick={() => addGiornata(ei)} className="mt-3 inline-flex items-center gap-1.5 text-sm font-600 text-[#EA6A0A] hover:underline"><Plus size={15} /> Aggiungi giornata</button>
        </div>
      ))}
      <button type="button" data-testid="edition-add" onClick={addEdition} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-dashed border-[#EA6A0A]/40 text-sm font-600 text-[#EA6A0A] hover:bg-[#EA6A0A]/5 transition-colors"><Plus size={16} /> Aggiungi edizione</button>
    </div>
  );
}

function FormModal({ cfg, initial, cats, galleryCats, extra, onClose, onSave }) {
  const fields = React.useMemo(() => cfg.fields(cats || [], galleryCats || [], extra || {}), [cfg, cats, galleryCats, extra]);
  const [form, setForm] = React.useState(() => {
    const f = { ...initial };
    fields.forEach((fl) => {
      if (f[fl.key] === undefined) {
        f[fl.key] = fl.type === "list" ? []
          : fl.type === "editions" ? [{ id: rid(), giornate: [emptyGiornata()] }]
          : fl.type === "checkbox" ? (fl.key === "published" || fl.key === "is_published" ? true : false)
          : "";
      }
    });
    if (Array.isArray(f.editions)) {
      f.editions = f.editions.map((ed) => ({ ...ed, status: ed.status || f.status || "available" }));
    }
    return f;
  });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [uploading, setUploading] = React.useState({});
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const payload = { ...form };
    fields.forEach((fl) => { if (fl.type === "number") payload[fl.key] = Number(payload[fl.key]) || 0; });
    try { await onSave(payload); }
    catch (ex) { setErr(ex.response?.data?.detail || "Errore nel salvataggio."); }
    finally { setBusy(false); }
  };

  const onFile = (k, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [k]: true }));
    setErr("");
    uploadImage(file)
      .then((url) => set(k, url))
      .catch((ex) => setErr(formatApiErrorDetail(ex.response?.data?.detail) || "Errore nel caricamento immagine."))
      .finally(() => setUploading((u) => ({ ...u, [k]: false })));
  };

  return (
    <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <motion.form onSubmit={submit} initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        className="relative bg-white rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-form">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-800 text-xl text-gray-900">{form.id ? "Modifica" : "Nuovo"} · {cfg.label}</h3>
          <button type="button" onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          {fields.map((fl) => (
            <div key={fl.key}>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide">{fl.label}</label>
              <div className="mt-1.5">
                {fl.type === "textarea" ? (
                  <textarea data-testid={`field-${fl.key}`} value={form[fl.key]} onChange={(e) => set(fl.key, e.target.value)} rows={3}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#EA6A0A]/20 focus:border-[#EA6A0A]" />
                ) : fl.type === "list" ? (
                  <textarea data-testid={`field-${fl.key}`} value={(form[fl.key] || []).join("\n")} onChange={(e) => set(fl.key, e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} rows={3}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#EA6A0A]/20 focus:border-[#EA6A0A]" />
                ) : fl.type === "editions" ? (
                  <EditionsEditor value={form[fl.key] || []} onChange={(v) => set(fl.key, v)} />
                ) : fl.type === "select" ? (
                  <select data-testid={`field-${fl.key}`} value={form[fl.key]} onChange={(e) => set(fl.key, e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#EA6A0A]/20 focus:border-[#EA6A0A]">
                    <option value="">— Seleziona —</option>
                    {fl.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : fl.type === "checkbox" ? (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input data-testid={`field-${fl.key}`} type="checkbox" checked={!!form[fl.key]} onChange={(e) => set(fl.key, e.target.checked)} className="w-5 h-5 accent-[#EA6A0A]" />
                    <span className="text-sm text-gray-600">Sì</span>
                  </label>
                ) : fl.type === "color" ? (
                  <div className="flex items-center gap-3">
                    <input data-testid={`field-${fl.key}`} type="color" value={form[fl.key] || "#EA6A0A"} onChange={(e) => set(fl.key, e.target.value)} className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer" />
                    <input type="text" value={form[fl.key] || ""} onChange={(e) => set(fl.key, e.target.value)} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EA6A0A]/20 focus:border-[#EA6A0A]" />
                  </div>
                ) : fl.type === "image" ? (
                  <div className="space-y-2">
                    {form[fl.key] && <img src={imgUrl(form[fl.key])} alt="" className="w-full h-36 object-cover rounded-xl border border-gray-100" />}
                    <div className="flex items-center gap-2">
                      <input data-testid={`field-${fl.key}-file`} type="file" accept="image/*" disabled={uploading[fl.key]} onChange={(e) => onFile(fl.key, e.target.files[0])}
                        className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-600 file:bg-[#EA6A0A]/10 file:text-[#EA6A0A] hover:file:bg-[#EA6A0A]/20 disabled:opacity-50" />
                      {uploading[fl.key] && <Loader2 size={18} className="animate-spin text-[#EA6A0A] shrink-0" />}
                    </div>
                    <input data-testid={`field-${fl.key}`} type="text" value={typeof form[fl.key] === "string" && !form[fl.key].startsWith("data:") ? form[fl.key] : ""} onChange={(e) => set(fl.key, e.target.value)} placeholder="oppure incolla un URL immagine"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EA6A0A]/20 focus:border-[#EA6A0A]" />
                  </div>
                ) : (
                  <input data-testid={`field-${fl.key}`} type={fl.type === "number" ? "number" : fl.type === "date" ? "date" : "text"} value={form[fl.key]} onChange={(e) => set(fl.key, e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#EA6A0A]/20 focus:border-[#EA6A0A]" />
                )}
              </div>
            </div>
          ))}
        </div>

        {err && <p className="mt-4 text-sm text-[#EF4444] bg-red-50 rounded-lg px-3 py-2">{err}</p>}

        <div className="mt-7 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-gray-200 font-600 text-gray-600 hover:bg-gray-50">Annulla</button>
          <button data-testid="admin-form-save" type="submit" disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-full bg-[#EA6A0A] text-white font-600 hover:bg-[#C8590A] disabled:opacity-60 transition-all">
            {busy && <Loader2 size={16} className="animate-spin" />} Salva
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}
